import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';

import { MessageService } from 'primeng/api';
import { filter, Subscription, withLatestFrom } from 'rxjs';

import { FileDownloadsApi } from '../../api/fileDownloads/file-downloads.api';
import { MediaServiceApi } from '../../api/mediaService/media-service.api';
import { messageSeverityEnum } from '../../Common/Misc/enums/message-severity.enum';
import { WebsocketAwareNeptuneService } from './websocket-aware-neptune.service';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../Core/Localization/services/localization.service';
import { AlertHistoryModel } from '../../Monitor/Alerts/models/alert-history.model';

enum AlertSentimentLocalization {
    increased = 'increased',
    decreased = 'increased',
}

@Injectable()
export class GlobalMessageService implements OnDestroy {

    subscriptions: Subscription[] = [];

    constructor(
        public websocketService: WebsocketAwareNeptuneService,
        public messageService: MessageService,
        public mediaServiceApi: MediaServiceApi,
        public fileDownloadsApi: FileDownloadsApi,
        private currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        this.subscriptions.push(
            // Driven by the websocket alone, with the account only gating it: on combineLatest a
            // reloaded account - which any AMT save does - re-emitted the last message and showed
            // its growl again
            this.websocketService.getWebsocketMessages().pipe(
                filter(x => !!x),
                withLatestFrom(this.currentUserStateService.getAccount().pipe(filter(x => !!x)))
            ).subscribe(([message])=> {
                if (!document.hidden) {
                    if (message?.resourceType === 'media') {
                        this.broadcastFileGenerationCompleteMessage(message);
                    }
                    if (message?.resourceType === 'filedownloads/monitoring') {
                        this.broadcastFileGenerationFailedMessage(message);
                    }
                }
                if (message?.resourceType === 'SURGE_ALERT') {
                    this.showSurgeAlertNotification(message);
                }
                if (message?.resourceType === 'auto-save') {
                    this.broadcastAutoSavingMentionsMessage();
                }
                if (message?.resourceType === 'high-volume-warning') {
                    this.broadcastAutoSavingHighVolumeMessage(message);
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    broadcastFileGenerationCompleteMessage(message: any): void {
        const mediaId = message.slug.split('ids=')[1];
        this.mediaServiceApi.getFiles([mediaId]).pipe(filter(x => !!x)).subscribe(file => {
            const downloadLink = file.mediaResults[0].url;
            const fileName = file.mediaResults[0].name;
            const reviewLink = this.appBaseHref + 'FileDownloads';
            if (downloadLink) {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    data: {
                        html: `
                            <div style="line-height: 1.125rem">
                                <div>File Ready for Download</div>
                                <div>The file "${fileName}" is ready to be downloaded.</div>
                                <div>
                                    <a href="${reviewLink}" target="_blank" style="color: #292929">Review</a>
                                    <a href="${downloadLink}" style="color: #292929" download>Download</a>
                                </div>
                            </div>
                        `
                    },
                    sticky: true
                });
            }
        });
    }

    broadcastFileGenerationFailedMessage(message: any): void {
        const uuid = message.slug.split('filedownloads/monitoring/')[1];
        if (!uuid) {
            return;
        }
        this.fileDownloadsApi.getFile(uuid).subscribe(file => {
            const fileName = file.name;
            const reviewLink = this.appBaseHref + 'FileDownloads';
            this.messageService.add({
                severity: messageSeverityEnum.error,
                data: {
                    html: `
                        <div style="line-height: 1.125rem">
                            <div>File Generation Failed</div>
                            <div>The file failed to generate</div>
                            <div>"${fileName}"</div>
                            <div><a href="${reviewLink}" target="_blank" style="color: #fff">Review</a></div>
                        </div>
                    `
                },
                sticky: true
            });
        });
    }

    showSurgeAlertNotification(message) {
        if (message?.surgeAlertTwigData) {
            const surgeAlertTwigData: AlertHistoryModel = new AlertHistoryModel(message?.surgeAlertTwigData);

            if (surgeAlertTwigData.surgePercentageChangePositive) {
                this.displayMessage(surgeAlertTwigData, surgeAlertTwigData.surgePercentageChangePositive, false);
            }
            if (surgeAlertTwigData.surgePercentageChangeNegative) {
                this.displayMessage(surgeAlertTwigData, surgeAlertTwigData.surgePercentageChangeNegative, true);
            }
        }
    }

    displayMessage(surgeAlertTwigData: AlertHistoryModel, percent: number, isNegativeSentiment: boolean) {
        const alertDashboardLink = this.appBaseHref +
            `Monitoring/alerts/alert/${surgeAlertTwigData.briefingId}/${surgeAlertTwigData.guid}/report`;
        const baseMessage = isNegativeSentiment ?
            `Alerts.surge.dashboard.tooltip.negative.${surgeAlertTwigData.metric}` : `Alerts.surge.dashboard.tooltip.${surgeAlertTwigData.metric}`;
        const changeWord = this.localizationService.transform(
            percent >= 100 ? 'Alerts.surge.dashboard.tooltip.increased' : 'Alerts.surge.dashboard.tooltip.decreased'
        );

        const message = this.localizationService.transform(
            baseMessage,
            {
                alertName: surgeAlertTwigData.alertName,
                changeWord: changeWord,
                percent: percent
            }
        );
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: surgeAlertTwigData.alertName + ' - ' +
                this.localizationService.transform('Alerts.surge.dashboard.title.' + surgeAlertTwigData.metric),
            data: {
                html: `
                    <div style="line-height: 1.125rem">
                        <div>${message}</div>
                        <div><a href="${alertDashboardLink}" target="_blank" style="color: #292929">View Details</div></div>
                    </div>
                `
            },
            sticky: true
        });
    }

    broadcastAutoSavingMentionsMessage() {
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Monitor.autoSaving.successfullMessage'),
            detail: this.localizationService.transform('Monitor.autoSaving.successfullMessage.details')
        });
    }

    broadcastAutoSavingHighVolumeMessage(message) {
        this.messageService.add({
            severity: messageSeverityEnum.warning,
            summary: this.localizationService.transform('Monitor.autoSaving.highVolumeWarning'),
            detail: this.localizationService.transform('Monitor.autoSaving.highVolumeWarning.details', {name: message.topicData.topicName})
        });
    }
}
